import {
  createRootRouteWithContext,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/auth'

// Layouts
import { AuthLayout } from '@/components/layout/AuthLayout'
import { AppLayout } from '@/components/layout/AppLayout'

// Pages
import { LoginPage } from '@/app/routes/login'
import { RegisterPage } from '@/app/routes/register'
import { ForgotPasswordPage } from '@/app/routes/forgot-password'
import { DashboardPage } from '@/app/routes/dashboard'
import { JobsPage } from '@/app/routes/jobs'
import { JobDetailPage } from '@/app/routes/jobs/[id]'
import { NewJobPage } from '@/app/routes/jobs/new'
import { EditJobPage } from '@/app/routes/jobs/edit'
import { CandidatesPage } from '@/app/routes/candidates'
import { CandidateDetailPage } from '@/app/routes/candidates/[id]'
import { NewCandidatePage } from '@/app/routes/candidates/new'
import { ApplicationsPage } from '@/app/routes/applications'
import { NewApplicationPage } from '@/app/routes/applications/new'
import { InterviewsPage } from '@/app/routes/interviews'
import { NewInterviewPage } from '@/app/routes/interviews/new'
import { ReportsPage } from '@/app/routes/reports'
import { SettingsPage } from '@/app/routes/settings'

// Public Career Pages
import { CareerPage } from '@/app/routes/careers/[tenant]/index'
import { PublicJobDetailPage } from '@/app/routes/careers/[tenant]/jobs/[slug]'

interface RouterContext {
  queryClient: QueryClient
}

// Root route
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
})

// Auth layout route (login, register)
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_auth',
  component: AuthLayout,
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState()
    if (isAuthenticated) {
      throw redirect({ to: '/' })
    }
  },
})

// App layout route (authenticated pages)
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: AppLayout,
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState()
    if (!isAuthenticated) {
      throw redirect({ to: '/login' })
    }
  },
})

// Auth routes
const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login',
  component: LoginPage,
})

const registerRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/register',
  component: RegisterPage,
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
})

// App routes
const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: DashboardPage,
})

const jobsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs',
  component: JobsPage,
})

const jobDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/$jobId',
  component: JobDetailPage,
})

const newJobRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/new',
  component: NewJobPage,
})

const editJobRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/$jobId/edit',
  component: EditJobPage,
})

const candidatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/candidates',
  component: CandidatesPage,
})

const newCandidateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/candidates/new',
  component: NewCandidatePage,
})

const candidateDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/candidates/$candidateId',
  component: CandidateDetailPage,
})

const applicationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/applications',
  component: ApplicationsPage,
})

const newApplicationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/applications/new',
  component: NewApplicationPage,
})

const interviewsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews',
  component: InterviewsPage,
})

const newInterviewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews/new',
  component: NewInterviewPage,
})

const reportsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports',
  component: ReportsPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: SettingsPage,
})

// Public career page routes (no auth required)
const careerPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/careers/$tenant',
  component: CareerPage,
})

const careerJobDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/careers/$tenant/jobs/$slug',
  component: PublicJobDetailPage,
})

// Build route tree
export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute, registerRoute, forgotPasswordRoute]),
  appLayoutRoute.addChildren([
    dashboardRoute,
    jobsRoute,
    newJobRoute,
    jobDetailRoute,
    editJobRoute,
    candidatesRoute,
    newCandidateRoute,
    candidateDetailRoute,
    applicationsRoute,
    newApplicationRoute,
    interviewsRoute,
    newInterviewRoute,
    reportsRoute,
    settingsRoute,
  ]),
  // Public career pages (no auth required)
  careerPageRoute,
  careerJobDetailRoute,
])
